import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { api } from "@/lib/api";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: diff <= 0 };
}

const Unit = ({ value, label }) => (
  <div className="flex flex-col items-center leading-none">
    <span className="font-display text-xl md:text-2xl tabular-nums">{String(value).padStart(2, "0")}</span>
    <span className="font-mono-ui text-[0.5rem] uppercase tracking-widest opacity-70">{label}</span>
  </div>
);

export default function DropBanner() {
  const [drop, setDrop] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();
  const hidden = location.pathname.startsWith("/admin") || location.pathname.startsWith("/unsubscribe");

  useEffect(() => {
    if (hidden) return;
    api.get("/drop-countdown").then(({ data }) => { if (data.active) setDrop(data); }).catch(() => {});
  }, [hidden]);

  const active = !!drop?.active && !dismissed && !hidden;

  return (
    <AnimatePresence>
      {active && <Bar drop={drop} onClose={() => setDismissed(true)} onExpire={() => setDrop(null)} />}
    </AnimatePresence>
  );
}

function Bar({ drop, onClose, onExpire }) {
  const { d, h, m, s, done } = useCountdown(drop.drop_at);
  useEffect(() => { if (done) onExpire(); }, [done, onExpire]);
  if (done) return null;
  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 80, opacity: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
      data-testid="drop-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[55] w-[calc(100%-2rem)] max-w-2xl"
    >
      <div className="flex items-center gap-4 bg-[#DFFF00] text-[#090909] border-2 border-[#090909] px-4 py-2.5 shadow-[6px_6px_0_0_#090909]">
        <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.2em] shrink-0 hidden sm:block">Dropping in</span>
        <div className="flex items-center gap-3">
          <Unit value={d} label="days" />
          <span className="font-display text-lg opacity-40">:</span>
          <Unit value={h} label="hrs" />
          <span className="font-display text-lg opacity-40">:</span>
          <Unit value={m} label="min" />
          <span className="font-display text-lg opacity-40">:</span>
          <Unit value={s} label="sec" />
        </div>
        <p className="font-mono-ui text-xs uppercase tracking-wide truncate flex-1 border-l-2 border-[#090909]/30 pl-4 hidden md:block" data-testid="drop-banner-headline">{drop.headline}</p>
        <button onClick={onClose} data-testid="drop-banner-close" aria-label="Dismiss" className="shrink-0 hover:opacity-60 transition-opacity">
          <X size={18} strokeWidth={2.5} />
        </button>
      </div>
    </motion.div>
  );
}
